import { useState } from "react";
import {
  SUBSCRIPTION_PLANS,
  TRANSFER_PLAN_CORRECTION_REASON_MAX,
  type SubscriptionPlan,
  type TransferPlanChoiceView,
  type TransferReview,
} from "@care-by-ella/contracts";
import {
  correctTransferPlan,
  ForbiddenError,
  UnauthorizedError,
} from "@/lib/clinician-api";
import {
  eligibilityLine,
  patientChoiceLine,
  planLabel,
  type PlanAmounts,
} from "@/lib/transfer-plan";
import { useReadOnly } from "./SupportView";
import { useUnderhallEligibility } from "./PatientEarlierTreatmentStart";
import { underhallBlockedText } from "@/lib/earlier-treatment-start";

// TES-1034 S8 (Emelie, points 5-6) — the patient's subscription choice in the
// transfer review. A patient choice, not a medical decision: Kirill sees it,
// does not choose or approve it, and the right link follows it. He may
// correct it as an exception, with a short reason, before the review is
// decided; the correction is logged (old and new option, who, when) and
// shown here and in the decision dialog.

function dateSv(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "Europe/Stockholm",
  });
}

export function TransferPlanChoiceCard({
  patientId,
  view,
  amounts,
  canCorrect,
  onCorrected,
  onUnauthorized,
}: {
  patientId: string;
  view: TransferPlanChoiceView;
  amounts: PlanAmounts;
  // Only while the review is open: after the decision the link is sent.
  canCorrect: boolean;
  onCorrected: (review: TransferReview) => void;
  onUnauthorized: () => void;
}) {
  // TES-1040 — no "Korrigera valet" in the admin's read-only view.
  const readOnly = useReadOnly();
  const [editing, setEditing] = useState(false);
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const unclear = eligibilityLine(view);
  // TES-1073 (Ö-297) — "Ella Underhåll" needs twelve documented months; the
  // server refuses the correction and the approval's link without them.
  const underhall = useUnderhallEligibility(patientId);
  const blocked = underhall === null ? null : underhallBlockedText(underhall);

  async function save() {
    if (!plan || !reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await correctTransferPlan(patientId, {
        plan,
        reason: reason.trim(),
      });
      setEditing(false);
      setPlan(null);
      setReason("");
      onCorrected(updated);
    } catch (e) {
      if (e instanceof UnauthorizedError) onUnauthorized();
      // TES-1073 — a 403 carries its reason in words (the "Underhåll" block).
      else if (e instanceof ForbiddenError) setError(e.message);
      else setError("Korrigeringen kunde inte sparas. Försök igen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      aria-label="Abonnemangsval"
      className="mb-lg rounded-md border border-surfaceMuted p-md"
      data-testid="transfer-plan-choice"
    >
      <h3 className="text-xs font-semibold text-textSecondary uppercase tracking-wide mb-xs">
        Abonnemang (patientens val, inte ett medicinskt beslut)
      </h3>
      <p className="text-sm font-semibold">
        {patientChoiceLine(view, amounts)}
      </p>
      {unclear ? (
        <p className="text-xs text-textSecondary mt-xs">{unclear}</p>
      ) : null}
      {view.correction ? (
        <p className="mt-sm rounded-md bg-warning/10 p-sm text-sm">
          <span className="font-semibold">
            Korrigerat av läkare: {planLabel(view.correction.plan, amounts)}
          </span>
          <span className="block text-xs text-textSecondary">
            {view.correction.correctedBy ?? "Läkare"} ·{" "}
            {dateSv(view.correction.correctedAt)} · Orsak:{" "}
            {view.correction.reason}
          </span>
        </p>
      ) : null}
      {canCorrect && blocked && view.effectivePlan === "ella_underhall" ? (
        <p
          className="mt-sm rounded-md bg-warning/10 p-sm text-xs"
          data-testid="transfer-underhall-blocked"
        >
          {blocked} Ett exakt startdatum minst tolv månader bakåt i godkännandet
          räknas också; annars stoppas godkännandet. Korrigera till Ella
          Uppstart eller registrera behandlingsstarten ovan.
        </p>
      ) : null}
      {canCorrect && !editing && !readOnly ? (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="mt-sm text-xs text-textSecondary underline hover:text-textPrimary"
        >
          Korrigera valet
        </button>
      ) : null}
      {editing ? (
        <div className="mt-sm flex flex-col gap-sm rounded-md bg-surfaceMuted/40 p-sm">
          <p className="text-xs text-textSecondary">
            Endast om patienten har ändrat sig eller det finns ett konkret skäl.
            Korrigeringen loggas med tidigare och nytt alternativ.
          </p>
          <fieldset className="flex flex-col gap-xs text-sm">
            <legend className="sr-only">Nytt alternativ</legend>
            {SUBSCRIPTION_PLANS.map((p) => {
              const reason = p === "ella_underhall" ? blocked : null;
              return (
                <label
                  key={p}
                  className={`flex items-start gap-sm ${reason ? "opacity-60" : ""}`}
                >
                  <input
                    type="radio"
                    name="transfer-plan-correction"
                    checked={plan === p}
                    onChange={() => setPlan(p)}
                    disabled={
                      busy ||
                      reason !== null ||
                      (p === "ella_underhall" && underhall === null)
                    }
                    className="mt-[3px]"
                  />
                  <span>
                    {planLabel(p, amounts)}
                    {reason ? (
                      <span className="block text-xs text-warning">
                        {reason}
                      </span>
                    ) : null}
                  </span>
                </label>
              );
            })}
          </fieldset>
          <label className="flex flex-col gap-xs text-sm">
            <span className="text-textSecondary">Orsak (krävs)</span>
            <textarea
              value={reason}
              maxLength={TRANSFER_PLAN_CORRECTION_REASON_MAX}
              onChange={(e) => setReason(e.target.value)}
              className="min-h-[60px] rounded-md border border-surfaceMuted p-sm text-sm"
              disabled={busy}
            />
          </label>
          {error ? <p className="text-sm text-danger">{error}</p> : null}
          <div className="flex gap-sm">
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy || !plan || !reason.trim()}
              className="rounded-md bg-textPrimary px-md py-xs text-sm text-surface disabled:opacity-50"
            >
              {busy ? "Sparar…" : "Spara korrigering"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setError(null);
              }}
              disabled={busy}
              className="rounded-md px-md py-xs text-sm text-textSecondary hover:bg-surfaceMuted"
            >
              Avbryt
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
